import {
    DefaultButton,
    Dialog, DialogFooter, DialogType, MessageBar, MessageBarType, PrimaryButton, TextField,
} from '@fluentui/react'
import React, {
    useCallback, useEffect, useRef, useState,
} from 'react'
import Status from 'types/status'
import VehicleEquipment, { ErrorVehicleEquipment } from 'requests/objects/vehicleEquipment'
// @ts-ignore
// eslint-disable-next-line import/named
import { RequestApi } from 'requests/apiHandler'
import CancelRequestError from 'requests/errors/cancelRequestError'
import UnauthorizedError from 'requests/errors/unauthorizedError'
import InvalidEntityError from 'requests/errors/invalidEntityError'
import NotImplementedError from 'requests/errors/notImplementedError'
import useTranslate from 'helpers/hooks/useTranslate'
import CompaniesHandler from 'requests/handlers/companiesHandler'
import usePrevious from 'helpers/hooks/usePrevious'
import parseJson from 'helpers/methods/parseJson'

/**
 * Vehicle equipment modal
 * @param {object} props Props
 * @param {boolean} props.isVisible isVisible
 * @param {CompaniesHandler} props.companiesHandler companiesHandler
 * @param {Partial<VehicleEquipment>} props.vehicleEquipmentData vehicleEquipmentData
 * @param {(vehicleEquipment: VehicleEquipment) => void} props.onChange Callback to complete the vehicle equipment form
 * @param {string} props.lang lang
 * @returns {JSX.Element} vehicle equipment modal form to create/update vehicle equipment
 */
export default function VehicleEquipmentModal({
    isVisible,
    companiesHandler,
    vehicleEquipmentData,
    onChange,
    lang,
}) {
    const [vehicleEquipment, setVehicleEquipment] = useState(new VehicleEquipment(vehicleEquipmentData))
    const [errorMessage, setErrorMessage] = useState('')
    const [status, setStatus] = useState(Status.IDLE)
    const [errorField, setErrorField] = useState(new ErrorVehicleEquipment())
    const { tKey, tObj } = useTranslate({ lang })

    /** @type {React.MutableRefObject<RequestApi<VehicleEquipment>>} */
    const upsertVehicleEquipmentHandler = useRef()

    const prevIsVisible = usePrevious(isVisible)

    useEffect(() => {
        if (isVisible && prevIsVisible !== isVisible) {
            setErrorMessage(null)
            setErrorField(new ErrorVehicleEquipment())
            setVehicleEquipment(new VehicleEquipment(vehicleEquipmentData))
        }
    }, [vehicleEquipmentData, isVisible, prevIsVisible])

    const onSubmit = useCallback(async () => {
        try {
            setStatus(Status.PENDING)
            upsertVehicleEquipmentHandler.current = companiesHandler.upsertVehicleEquipment(
                vehicleEquipment.companyId,
                vehicleEquipment,
                vehicleEquipment.vehicleEquipmentId,
            )
            const vehicleEquipmentResult = await upsertVehicleEquipmentHandler.current.fetch()
            onChange(vehicleEquipmentResult)
            setStatus(Status.RESOLVED)
        } catch (error) {
            switch (error?.constructor) {
                case CancelRequestError:
                case UnauthorizedError:
                case NotImplementedError:
                    break
                case InvalidEntityError:
                    setErrorField(/** @type {InvalidEntityError<ErrorVehicleEquipment>} */(error).errorField)
                    // eslint-disable-next-line no-console
                    console.error(error)
                    setStatus(Status.REJECTED)
                    break
                default:
                    setStatus(Status.REJECTED)
                    setErrorMessage(error)
                    break
            }
        }
    }, [companiesHandler, vehicleEquipment, onChange])

    useEffect(() => () => {
        upsertVehicleEquipmentHandler.current?.cancel()
    }, [])

    return (
        <div>
            <Dialog
                hidden={!isVisible}
                dialogContentProps={{
                    type: DialogType.largeHeader,
                    title: vehicleEquipment?.vehicleEquipmentId ? tKey('modalEditVehicleEquipmentTitle') : tKey('modalVehicleEquipmentTitle'),
                }}
                modalProps={{
                    isBlocking: true,
                }}
                maxWidth="555px"
                minWidth="400px"
            >
                {errorMessage
                    && (
                        <MessageBar
                            messageBarType={MessageBarType.error}
                            isMultiline={false}
                            truncated
                            onDismiss={() => setErrorMessage(null)}
                        >
                            {tObj(errorMessage)}
                        </MessageBar>
                    )}
                <form
                    onSubmit={ev => {
                        ev.preventDefault()
                        onSubmit()
                    }}
                    style={{ display: 'flex', gap: '30px' }}
                />
                <TextField
                    label={tKey('nameFr')}
                    placeholder={tKey('nameFr')}
                    value={parseJson(vehicleEquipment.name)?.fr || ''}
                    errorMessage={tObj(parseJson(errorField.name))}
                    onChange={(ev, newVal) => {
                        setVehicleEquipment({
                            ...vehicleEquipment,
                            name: JSON.stringify({ ...parseJson(vehicleEquipment.name), fr: newVal }),
                        })
                    }}
                    required
                />
                <TextField
                    label={tKey('nameEn')}
                    placeholder={tKey('nameEn')}
                    value={parseJson(vehicleEquipment.name)?.en || ''}
                    errorMessage={tObj(parseJson(errorField.name))}
                    onChange={(ev, newVal) => {
                        setVehicleEquipment({
                            ...vehicleEquipment,
                            name: JSON.stringify({ ...parseJson(vehicleEquipment.name), en: newVal }),
                        })
                    }}
                    required
                />
                <TextField
                    label={tKey('nameEs')}
                    placeholder={tKey('nameEs')}
                    value={parseJson(vehicleEquipment.name)?.es || ''}
                    errorMessage={tObj(parseJson(errorField.name))}
                    onChange={(ev, newVal) => {
                        setVehicleEquipment({
                            ...vehicleEquipment,
                            name: JSON.stringify({ ...parseJson(vehicleEquipment.name), es: newVal }),
                        })
                    }}
                    required
                />
                <TextField
                    label={tKey('nameNl')}
                    placeholder={tKey('nameNl')}
                    value={parseJson(vehicleEquipment.name)?.nl || ''}
                    errorMessage={tObj(parseJson(errorField.name))}
                    onChange={(ev, newVal) => {
                        setVehicleEquipment({
                            ...vehicleEquipment,
                            name: JSON.stringify({ ...parseJson(vehicleEquipment.name), nl: newVal }),
                        })
                    }}
                    required
                />
                <br />
                <TextField
                    label={tKey('tmsCode')}
                    placeholder={tKey('tmsCode')}
                    value={vehicleEquipment.vehicleEquipmentTmsId || ''}
                    errorMessage={tObj(parseJson(errorField.vehicleEquipmentTmsId))}
                    onChange={(ev, newVal) => {
                        setVehicleEquipment({
                            ...vehicleEquipment,
                            vehicleEquipmentTmsId: newVal,
                        })
                    }}
                />
                <DialogFooter>
                    <DefaultButton
                        onClick={() => onChange(null)}
                        text={tKey('cancel')}
                        disabled={status === Status.PENDING}
                    />
                    <PrimaryButton
                        onClick={e => {
                            e.preventDefault()
                            onSubmit()
                        }}
                        text={tKey('save')}
                    />
                </DialogFooter>
            </Dialog>
        </div>
    )
}
